import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Hook de ofertas com suporte a HITL.
 *
 * Convenção HITL: qualquer edição de campos "vivos" (resumo_estagio, data_fup, proximo_passo)
 * feita por humano deve registrar trilha (updated_at, updated_by) e, se havia valor IA,
 * preservar o original em *_original_ia + marcar fonte como 'ai_editado'.
 */
export const useOfertas = () => {
  const [ofertas, setOfertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOfertas = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from('ofertas')
        .select(`
          *,
          empresas!ofertas_empresa_id_fkey(id, empresa, company_name),
          areas!ofertas_area_id_fkey(id, nome, area_total_m2),
          colaboradores!ofertas_responsavel_id_fkey(id, nome, email)
        `)
        .order('updated_at', { ascending: false });

      if (err) throw err;
      setOfertas(data || []);
    } catch (err) {
      console.error('Erro ao buscar ofertas:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOfertas(); }, [fetchOfertas]);

  /**
   * Cria nova oferta. Campos HITL não precisam ser passados — nascem vazios.
   */
  const createOferta = async (payload) => {
    const clean = {
      empresa_id: payload.empresa_id,
      area_id: payload.area_id || null,
      nome: payload.nome?.trim(),
      descricao: payload.descricao?.trim() || null,
      status: payload.status || '2.0.Lead_Qualificado',
      responsavel_id: payload.responsavel_id || null,
      valor_proposto: payload.valor_proposto || null,
      data_envio_proposta: payload.data_envio_proposta || null,
      data_ultimo_contato: payload.data_ultimo_contato || null,
    };
    if (!clean.empresa_id || !clean.nome) {
      throw new Error('empresa_id e nome são obrigatórios');
    }
    const { data, error: err } = await supabase
      .from('ofertas')
      .insert(clean)
      .select()
      .single();
    if (err) throw err;
    await fetchOfertas();
    return { success: true, oferta: data };
  };

  /**
   * Atualiza oferta com trilha HITL automática.
   *
   * Para campos HITL (resumo_estagio, data_fup, proximo_passo), o chamador
   * deve incluir também os campos _updated_at, _updated_by, e opcionalmente
   * _fonte e _original_ia. Use os helpers buildHITLPatch* abaixo para conveniência.
   *
   * Exemplo de uso manual:
   *   updateOferta(id, {
   *     resumo_estagio: 'Novo texto editado',
   *     resumo_estagio_fonte: 'ai_editado',  // se veio de IA e foi editado
   *     resumo_estagio_updated_at: new Date().toISOString(),
   *     resumo_estagio_updated_by: user.email
   *   });
   */
  const updateOferta = async (id, patches) => {
    if (!id) throw new Error('id da oferta é obrigatório');

    // Otimistic update: reflete mudança imediatamente na UI, antes do round-trip.
    // Preserva joins (empresas, areas, colaboradores) que o select('*, joins') traz.
    setOfertas(prev => prev.map(o => o.id === id ? { ...o, ...patches } : o));

    const { data, error: err } = await supabase
      .from('ofertas')
      .update(patches)
      .eq('id', id)
      .select(`
        *,
        empresas!ofertas_empresa_id_fkey(id, empresa, company_name),
        areas!ofertas_area_id_fkey(id, nome),
        colaboradores!ofertas_responsavel_id_fkey(id, nome, email)
      `)
      .single();

    if (err) {
      // Reverte o otimistic update com o estado autoritativo do servidor.
      await fetchOfertas();
      throw err;
    }

    // Aplica row autoritativa (inclui campos derivados como updated_at do servidor).
    setOfertas(prev => prev.map(o => o.id === id ? data : o));
    return { success: true, oferta: data };
  };

  /**
   * Helper: monta patch HITL para editar um campo manualmente.
   *
   * @param {string} campo - Nome do campo (resumo_estagio | proximo_passo)
   * @param {string} novoValor - Valor a salvar
   * @param {object} ofertaAtual - Oferta atual (para detectar se sobrescreve valor IA)
   * @param {string} userEmail - Email do editor
   * @returns {object} Patch pronto para passar a updateOferta
   */
  const buildHITLPatchManual = (campo, novoValor, ofertaAtual, userEmail) => {
    const fonteAtual = ofertaAtual?.[`${campo}_fonte`];
    const valorAtualIA = ofertaAtual?.[`${campo}_original_ia`];
    const valorAtual = ofertaAtual?.[campo];
    
    // Se valor atual veio de IA e ainda não havia _original_ia salvo, preserva agora
    const deveSalvarOriginalIA = 
      (fonteAtual === 'ai_briefing' || fonteAtual === 'ai_detector') 
      && !valorAtualIA 
      && valorAtual;
    
    const patch = {
      [campo]: novoValor,
      [`${campo}_fonte`]: fonteAtual === 'manual' ? 'manual' : 'ai_editado',
      [`${campo}_updated_at`]: new Date().toISOString(),
      [`${campo}_updated_by`]: userEmail || 'desconhecido',
    };
    
    if (deveSalvarOriginalIA) {
      patch[`${campo}_original_ia`] = valorAtual;
    }
    
    return patch;
  };

  /**
   * Helper: monta patch HITL quando IA (briefing/detector) gera valor novo.
   */
  const buildHITLPatchIA = (campo, novoValor, fonte, agenteId) => {
    return {
      [campo]: novoValor,
      [`${campo}_fonte`]: fonte, // 'ai_briefing' | 'ai_detector'
      [`${campo}_original_ia`]: novoValor, // preserva o que IA gerou
      [`${campo}_updated_at`]: new Date().toISOString(),
      [`${campo}_updated_by`]: agenteId || fonte,
    };
  };

  /**
   * Helper: patch para data_fup (campo data, sem fonte, mas com trilha)
   */
  const buildHITLPatchDataFup = (novaData, userEmail) => ({
    data_fup: novaData,
    data_fup_updated_at: new Date().toISOString(),
    data_fup_updated_by: userEmail || 'desconhecido',
  });

  const deleteOferta = async (id) => {
    const { error: err } = await supabase.from('ofertas').delete().eq('id', id);
    if (err) throw err;
    await fetchOfertas();
    return { success: true };
  };

  return {
    ofertas,
    loading,
    error,
    createOferta,
    updateOferta,
    deleteOferta,
    refetch: fetchOfertas,
    // Helpers HITL
    buildHITLPatchManual,
    buildHITLPatchIA,
    buildHITLPatchDataFup,
  };
};
